import { rawAttr } from "@wiki/core";
import type { Node as ProseMirrorNode } from "prosemirror-model";

import { readStored, writeStored } from "../lib/storage";

/**
 * A toggle's open/closed state is per user and per browser and is deliberately
 * **not** in the document (`markdown-dialect.md` §6.10), so it lives in
 * `localStorage` keyed by the toggle's identity and by the document it is in.
 *
 * A toggle node only sometimes carries an `id`. When it does not, its title
 * text is the identity: stable across reloads, stable across collaborators
 * editing elsewhere in the document, and cheap to compute. Two toggles with the
 * same title in one document share a state; that is a cosmetic collision and
 * the alternative — writing ids into `data` — would change the document just by
 * opening it.
 */
export function toggleKey(node: ProseMirrorNode): string {
  const id = rawAttr(node, "id");
  if (typeof id === "string" && id !== "") return id;
  const title = node.firstChild?.textContent ?? "";
  return `t:${title.slice(0, 80)}`;
}

function storageKey(documentId: string | null, key: string): string {
  return `wiki.toggle.${documentId ?? "draft"}.${key}`;
}

const listeners = new Set<() => void>();

/** Toggles are open unless the user has closed this one. */
export function isToggleOpen(documentId: string | null, key: string): boolean {
  return readStored(storageKey(documentId, key)) !== "closed";
}

export function setToggleOpen(documentId: string | null, key: string, open: boolean): void {
  writeStored(storageKey(documentId, key), open ? "open" : "closed");
  for (const listener of listeners) listener();
}

/** Node views re-render from this when any toggle's state changes. */
export function subscribeToggleState(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
